import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { groupBy, isNil, sortBy } from "lodash";
import { HintDto } from "../core/hints/HintDto";
import { ItemLightDto } from "../core/items/ItemDataTypes";
import { GoalDto } from "./hints/GoalAutocomplete";
import { HintLocationDto } from "./hints/HintDataType";
import { OotItemDto } from "./hints/OotItemAutocomplete";
import {  LocationDto } from "./locations/LocationDtos";
import { ShopItemDto } from "./shop/ShopDataType";
import { EntranceTracking, StartingLocation } from "./mixpool/EntranceTracking";
import { Path } from "./mixpool/EntrancePathing";
import { Grottos, Interiors, OverworldEntrances } from "./mixpool/MixPoolNotes";

export interface OotrState {
    nbAlways: number,
    nbSometimes: number,
    nbGoals: number,
    nbBarren: number,
    barren: Array<LocationDto>,
    goals: { [key: number]: HintLocationDto },
    alwaysHints: Array<HintDto>,
    sometimesHints: Array<HintDto>
    items: Array<ItemLightDto>,

    players: { [key: number]: string }
    nbMultiworldPlayer: number
    //Settings
    isMultiworld: boolean
    isDungeonShuffle: boolean,
    isBossShuffle: boolean,
    isOcarinaNotShuffle: boolean,
    displayOnlyMajorItem: boolean,
    isBombchusInLogic: boolean,
    isBlueFireArrowActivated: boolean,
    isOpenFountainAlways: boolean,
    isSpawnAdultRandom: boolean,
    isSpawnChildRandom: boolean,
    isWarpSongRandom: boolean,
    isOwlRandom: boolean,
    isMixedPoolGrotto: boolean,
    isMixedPoolInterior: boolean,
    isMixedPoolDungeon: boolean,
    isMixedPoolOverworld: boolean,
    isWoth: boolean,
    isScrubsanity: boolean,
    isCowsanity: boolean,
    useScrubSettings: boolean,
    useTrials: boolean,
    shops: { [id: string]: Array<ShopItemDto> },
    useShopsanity: boolean,
    useItemTracker: boolean,
    useItemTrackerReduce: boolean,
    useLocationTracker: boolean,
    useSphereTracker: boolean,
    ocarinaShuffle: boolean,
    beanShuffle: boolean,
    kokiriSwordShuffle: boolean,
    gerudoCardShuffle: boolean,
    isKeysanity: boolean,
    isKeyring: boolean,
    isKeyringGiveBk: boolean,

    //checks reducer
    location: LocationDto | null,
    sublocation: LocationDto | null

    //mixedpool
    entrances: {[index: string]: EntranceTracking}
    accessibleStartingLocation: Array<StartingLocation>
    vanillaWarpLogic: boolean,
    mixedPoolPaths: Array<Array<Path>>
    highlightOverEntrances: Array<string>

    commentaryEditKey: Array<CommentaryEditData>

    //pot crate freestanding
    checkLocations: {[index: string]: Array<OotItemDto | null>}
}

interface CommentaryEditData {id: number, editKey: string}

export const initialOotrState: OotrState = {
    nbAlways: 7,
    nbSometimes: 7,
    nbGoals: 5,
    nbBarren: 3,
    barren: [],
    goals: {},
    players: {},
    alwaysHints: [],
    sometimesHints: [],
    items: [],
    isMultiworld: false,
    nbMultiworldPlayer: 3,
    isDungeonShuffle: false,
    isBossShuffle: false,
    isOcarinaNotShuffle: false,
    displayOnlyMajorItem: false,
    isBombchusInLogic: false,
    isBlueFireArrowActivated: false,
    isOpenFountainAlways: false,
    isSpawnAdultRandom: false,
    isSpawnChildRandom: false,
    isWarpSongRandom: false,
    isOwlRandom: false,
    isMixedPoolGrotto: false,
    isMixedPoolInterior: false,
    isMixedPoolDungeon: false,
    isMixedPoolOverworld: false,
    isWoth: false,
    isScrubsanity: false,
    isCowsanity: false,
    useScrubSettings: false,
    useTrials: false,
    shops: {},
    useShopsanity: false,
    useItemTracker: true,
    useItemTrackerReduce: false,
    useLocationTracker: true,
    useSphereTracker: false,
    ocarinaShuffle: false,
    beanShuffle: false,
    kokiriSwordShuffle: true,
    gerudoCardShuffle: false,
    isKeysanity: false,
    isKeyring: false,
    isKeyringGiveBk: false,


    //checks
    location: null,
    sublocation: null,

    //mixedpool
    entrances: {},
    accessibleStartingLocation: [],
    vanillaWarpLogic: true,
    mixedPoolPaths: [],
    highlightOverEntrances: [],
    commentaryEditKey: [],

    //pot crate freestanding
    checkLocations: {}
}

export const ootrSlice = createSlice({
    name: 'ootr tracker reducer',
    initialState: initialOotrState,
    reducers: {
        setNbAlways(state: OotrState, action: PayloadAction<number>) {
            state.nbAlways = action.payload
        },
        setNbSometimes(state: OotrState, action: PayloadAction<number>) {
            state.nbSometimes = action.payload
        },
        setNbGoals(state: OotrState, action: PayloadAction<number>) {
            state.nbGoals = action.payload
        },
        setNbBarren(state: OotrState, action: PayloadAction<number>) {
            state.nbBarren = action.payload
        },
        setItems(state: OotrState, action: PayloadAction<ItemLightDto>) {

            if (!isNil(state.items.find(i => i.id === action.payload.id))) {
                state.items = [...state.items.filter(i => i.id !== action.payload.id), action.payload]
            }
            else
                state.items = [...state.items, action.payload]
        },
        setBarren(state: OotrState, action: PayloadAction<{ barren: LocationDto | null, id: number }>) {

            if (isNil(action.payload.barren)) {
                state.barren = [...state.barren.filter((item, i) => i !== action.payload?.id)]
                return;
            }

            state.barren = [...state.barren.slice(0, action.payload?.id),
            action.payload?.barren,
            ...state.barren.slice(action.payload?.id)]
        },
        setGoalLocations(state: OotrState, action: PayloadAction<{ location: LocationDto | null, items: Array<OotItemDto | null>, id: number, goal: GoalDto | null, count: number, player: string | null }>) {
            state.goals[action.payload.id] = action.payload

        },
        setIsDungeonShuffle(state: OotrState, action: PayloadAction<boolean>) {
            state.isDungeonShuffle = action.payload;
        },
        setIsBossShuffle(state: OotrState, action: PayloadAction<boolean>) {
            state.isBossShuffle = action.payload;
        },
        setIsCowsanity(state: OotrState, action: PayloadAction<boolean>) {
            state.isCowsanity = action.payload;
        },
        setIsScrubsanity(state: OotrState, action: PayloadAction<boolean>) {
            state.isScrubsanity = action.payload
        },
        setIsOcarinaNoteShuffle(state: OotrState, action: PayloadAction<boolean>) {
            state.isOcarinaNotShuffle = action.payload;
        },
        setDisplayOnlyMajorItem(state: OotrState, action: PayloadAction<boolean>) {
            state.displayOnlyMajorItem = action.payload
        },
        setIsBombchusInLogic(state: OotrState, action: PayloadAction<boolean>) {
            state.isBombchusInLogic = action.payload
        },
        setIsBlueFireArrowActivated(state: OotrState, action: PayloadAction<boolean>) {
            state.isBlueFireArrowActivated = action.payload
        },
        setIsOpenFountainAlways(state: OotrState, action: PayloadAction<boolean>) {
            state.isOpenFountainAlways = action.payload
        },
        setIsSpawnAdultRandom(state: OotrState, action: PayloadAction<boolean>) {
            state.isSpawnAdultRandom = action.payload
        },
        setIsSpawnChildRandom(state: OotrState, action: PayloadAction<boolean>) {
            state.isSpawnChildRandom = action.payload
        },
        setIsWarpSongRandom(state: OotrState, action: PayloadAction<boolean>) {
            state.isWarpSongRandom = action.payload
        },
        setIsOwlRandom(state: OotrState, action: PayloadAction<boolean>) {
            state.isOwlRandom = action.payload
        },
        setIsMixedPoolGrotto(state: OotrState, action: PayloadAction<boolean>) {
            state.isMixedPoolGrotto = action.payload
        },

        setIsMixedPoolInterior(state: OotrState, action: PayloadAction<boolean>) {
            state.isMixedPoolInterior = action.payload
        },
        setIsMixedPoolDungeon(state: OotrState, action: PayloadAction<boolean>) {
            state.isMixedPoolDungeon = action.payload
        },
        setIsMixedPoolOverworld(state: OotrState, action: PayloadAction<boolean>) {
            state.isMixedPoolOverworld = action.payload
        },
        setIsWoth(state: OotrState, action: PayloadAction<boolean>) {
            state.isWoth = action.payload
        },
        setUseScrubAlwaysHints(state: OotrState, action: PayloadAction<boolean>) {
            state.useScrubSettings = action.payload
        },
        setUseTrials(state: OotrState, action: PayloadAction<boolean>) {
            state.useTrials = action.payload
        },
        setUseShopsanity(state: OotrState, action: PayloadAction<boolean>) {
            state.useShopsanity = action.payload
        },
        setUseItemTracker(state: OotrState, action: PayloadAction<boolean>) {
            state.useItemTracker = action.payload
        },
        setUseItemTrackerReduce(state: OotrState, action: PayloadAction<boolean>) {
            state.useItemTrackerReduce = action.payload
        },
        setUseLocationTracker(state: OotrState, action: PayloadAction<boolean>) {
            state.useLocationTracker = action.payload
        },
        setUseSphereTracker(state: OotrState, action: PayloadAction<boolean>) {
            state.useSphereTracker = action.payload
        },
        setOcarinaShuffle(state: OotrState, action: PayloadAction<boolean>) {
            state.ocarinaShuffle = action.payload
        },
        setBeanShuffle(state: OotrState, action: PayloadAction<boolean>) {
            state.beanShuffle = action.payload
        },
        setKokiriSwordShuffle(state: OotrState, action: PayloadAction<boolean>) {
            state.kokiriSwordShuffle = action.payload
        },
        setGerudoCardShuffle(state: OotrState, action: PayloadAction<boolean>) {
            state.gerudoCardShuffle = action.payload
        },
        setIsMultiworld(state: OotrState, action: PayloadAction<boolean>) {
            state.isMultiworld = action.payload
        },
        setNbMultiworldPlayer(state: OotrState, action: PayloadAction<number>) {
            state.nbMultiworldPlayer = action.payload
        },
        setIsKeysanity(state: OotrState, action: PayloadAction<boolean>) {
            state.isKeysanity = action.payload
        },
        setIsKeyring(state: OotrState, action: PayloadAction<boolean>) {
            state.isKeyring = action.payload
        },
        setIsKeyringGiveBk(state: OotrState, action: PayloadAction<boolean>) {
            state.isKeyringGiveBk = action.payload
        },
        setShopItem(state: OotrState, action: PayloadAction<{ item: OotItemDto | null, shop: string, itemid: number, price: number | null }>) {
            if (isNil(state.shops[action.payload.shop])) {
                state.shops[action.payload.shop] = [{ item: action.payload.item, price: action.payload.price }, { item: null, price: 0 }, { item: null, price: 0 }, { item: null, price: 0 }]
            }
            else {
                state.shops[action.payload.shop][action.payload.itemid] = { item: action.payload.item, price: action.payload.price }
            }
        },
        setChecksLocation(state: OotrState, action: PayloadAction<LocationDto | null>) {
            state.location = action.payload
        },
        setChecksSubLocation(state: OotrState, action: PayloadAction<LocationDto | null>) {
            state.sublocation = action.payload
        },


        setEntranceTracking(state:OotrState, action: PayloadAction<EntranceTracking>)
        {
            state.entrances[action.payload.id] =  action.payload;
        },
        setStartingLocations(state:OotrState, action: PayloadAction<Array<StartingLocation>>)
        {
            state.accessibleStartingLocation =  action.payload;
        },
        setVanillaWarpLogic(state:OotrState, action: PayloadAction<boolean>)
        {
            state.vanillaWarpLogic =  action.payload;
        },
        setMixedPoolPaths(state:OotrState, action: PayloadAction<Array<Array<Path>>>)
        {
            state.mixedPoolPaths =  action.payload;
        },
        resetMixedPool(state: OotrState){
            state.entrances = {}

            let grottosEntrance = [...Grottos, ...Interiors, ...OverworldEntrances];

            grottosEntrance = sortBy(grottosEntrance, (x) => x.globalArea);
          
          
            let dic = groupBy(grottosEntrance, (x) => x.zone);
            Object.values(dic).map(l => {
                l.map(g => {state.entrances[g.name] = {id: g.name, state: 0,
                    boss: 0,
                    destination: "",
                    reward: 0,
                    era: g.eraRestricted
                
                };
                return null;})
                return null;
            })


            state.accessibleStartingLocation = []
            state.vanillaWarpLogic = true
            state.mixedPoolPaths= []
        },
        setHighlightOverEntrances(state: OotrState, action: PayloadAction<Array<string>>){
            state.highlightOverEntrances= action.payload
        },
        resetMixedPoolPaths(state: OotrState){
            state.mixedPoolPaths= []
        },
        setCommentaryEditKey(state:OotrState, action: PayloadAction<{id: number, editKey: string}>)
        {
            console.log(state.commentaryEditKey)
            state.commentaryEditKey =  [...state.commentaryEditKey.filter(x => x.id !== action.payload.id), action.payload];
        },
        setCheckLocation(state:OotrState, action: PayloadAction<{loc: string, items: Array<OotItemDto | null>}>)
        {
            console.log(state.checkLocations[action.payload.loc], action.payload.items)
            if(isNil(state.checkLocations))
                state.checkLocations = {}
            state.checkLocations[action.payload.loc] =  action.payload.items;
        },
        setResetState(state: OotrState){
            state = initialOotrState
            resetMixedPool();
            resetMixedPoolPaths();
        }
    }
});

export const {
    setResetState,
    setNbAlways,
    setNbSometimes,
    setNbGoals,
    setNbBarren,
    setItems,
    setBarren,
    setGoalLocations,
    setIsDungeonShuffle,
    setIsBossShuffle,
    setIsCowsanity,
    setIsScrubsanity,
    setIsOcarinaNoteShuffle,
    setDisplayOnlyMajorItem,
    setIsBombchusInLogic,
    setIsBlueFireArrowActivated,
    setIsOpenFountainAlways,
    setIsSpawnAdultRandom,
    setIsSpawnChildRandom,
    setIsWarpSongRandom,
    setIsOwlRandom,
    setIsMixedPoolGrotto,
    setIsMixedPoolInterior,
    setIsMixedPoolDungeon,
    setIsMixedPoolOverworld,
    setIsWoth,
    setUseScrubAlwaysHints,
    setUseTrials,
    setShopItem,
    setUseShopsanity,
    setUseItemTracker,
    setUseItemTrackerReduce,
    setUseLocationTracker,
    setUseSphereTracker,
    setOcarinaShuffle,
    setBeanShuffle,
    setKokiriSwordShuffle,
    setGerudoCardShuffle,
    setIsMultiworld,
    setNbMultiworldPlayer,
    setIsKeysanity,
    setIsKeyring,
    setIsKeyringGiveBk,
    setChecksLocation,
    setChecksSubLocation,
    setEntranceTracking,
    setStartingLocations,
    setVanillaWarpLogic,
    setMixedPoolPaths,
    resetMixedPool,
    resetMixedPoolPaths,
    setHighlightOverEntrances,
    setCommentaryEditKey,
    setCheckLocation
} = ootrSlice.actions

export const ootrMainReducer = ootrSlice.reducer


